import { FormControl, TextField } from '@mui/material';
import { FC } from 'react';
import { TRtkErrorType } from 'data/types/general.types';
import { errorMessages, successMessages } from 'data/messages/messages';
import { SubmitHandler, useForm } from 'react-hook-form';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { NameSchema } from 'data/validation/CommonScemas';
import Modal from 'components/Base/Modal';

type TNameModalProps = {
  initialName?: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  submitButtonTitle?: string;
  onSubmitModal: (name: string) => Promise<void>;
  validationSchema?: any;
};

const NameModal: FC<TNameModalProps> = ({
  initialName,
  setOpen,
  open,
  modalTitle,
  submitButtonTitle = 'Save',
  onSubmitModal,
  validationSchema = NameSchema,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    handleSubmit,
    formState: { errors, defaultValues, isSubmitting },
    register,
  } = useForm<{
    name: string;
  }>({
    defaultValues: {
      name: initialName || '',
    },
    resolver: yupResolver(validationSchema),
  });

  const onClose = () => {
    setOpen(false);
  };

  const onSubmit: SubmitHandler<{ name: string }> = async values => {
    if (isEqual(values, defaultValues)) {
      onClose();
      return;
    }
    try {
      await onSubmitModal(values.name);
      enqueueSnackbar(successMessages.toast, {
        variant: 'success',
      });
      onClose();
    } catch (err) {
      const error = err as TRtkErrorType;
      enqueueSnackbar(error?.data?.message || errorMessages.default, {
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={modalTitle}
      submitButtonProps={{
        disabled: isSubmitting,
        onClick: handleSubmit(onSubmit),
        text: submitButtonTitle,
      }}
    >
      <FormControl
        component="form"
        fullWidth
        onSubmit={handleSubmit(onSubmit)}
        sx={{ pt: 1 }}
      >
        <TextField
          label="Name"
          variant="outlined"
          error={!!errors.name}
          helperText={errors.name && errors.name.message}
          required
          autoFocus
          fullWidth
          {...register('name')}
        />
      </FormControl>
    </Modal>
  );
};

export default NameModal;
